import React, { Component, Fragment } from 'react';
import {
  reduce, indexOf, map,
  isArray, slice,
} from 'lodash';
import {
  Button, ControlLabel, HelpBlock,
  FormGroup, FormControl, InputGroup,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ValueItem from './value-item';
import styles from './styles.css';

const mutatedReorder = (list, startIndex, endIndex) => {
  const [removed] = list.splice(startIndex, 1);
  list.splice(endIndex, 0, removed);
  return list;
};

class ValueList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      newLabel: null,
      editingMode: false,
      adding: false,
      errors: null,
      updating: false,
    };
    this.addNewRow = this.addNewRow.bind(this);
    this.handleValueChange = this.handleValueChange.bind(this);
    this.cancel = this.cancel.bind(this);
    this.showForm = this.showForm.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
  }

  onDragEnd(result) {
    const { updating } = this.state;
    if (!updating) {
      const { source, destination } = result;
      // dropped outside the list
      if (!destination) {
        return;
      }
      const list = [...this.props.values];
      mutatedReorder(
        list,
        source.index,
        destination.index,
      );
      const positions = map(list, 'value');
      this.setState({ updating: true });
      this.props.requestUpdatePositions(positions)
        .then(
          () => {
            this.setState({ updating: false });
          },
          () => {
            this.setState({ updating: false });
          },
        );
    }
  }
  handleValueChange(e) {
    this.setState({ newLabel: e.target.value, errors: null });
  }

  addNewRow() {
    const { adding } = this.state;
    if (!adding) {
      this.setState({ adding: true });
      this.props.requestNewValue({ label: this.state.newLabel })
        .then(
          () => {
            this.setState({ adding: false, editingMode: false, newLabel: null });
          },
          ({ data: { label } }) => {
            this.setState({ adding: false, errors: label });
          },
        );
    }
  }

  showForm() {
    this.setState({ newLabel: null, editingMode: true, errors: null });
  }
  cancel() {
    this.setState({ newLabel: null, editingMode: false, errors: null });
  }

  renderAddBtn() {
    return (
      <Button
        bsStyle="link"
        bsSize="sm"
        onClick={this.showForm}
      >
        <Fa name="plus" /> Add new value
      </Button>
    );
  }

  renderAddForm() {
    const { newLabel, adding, errors } = this.state;
    const { disabled } = this.props;
    const hasErrors = errors !== null;
    const validationState = hasErrors ? 'error' : null;
    return (
      <FormGroup validationState={validationState} style={{ margin: 0 }}>
        <InputGroup bsSize="sm">
          <FormControl
            type="text"
            value={newLabel || ''}
            disabled={disabled || adding}
            placeholder="enter new value label"
            autoFocus
            onChange={this.handleValueChange}
          />
          <InputGroup.Addon>
            <Button
              bsSize="sm"
              disabled={disabled || adding || !newLabel || newLabel.length === 0}
              onClick={this.addNewRow}
            >
              Add
            </Button>
            <Button
              bsSize="sm"
              bsStyle="link"
              disabled={disabled || adding}
              onClick={this.cancel}
            >
              Cancel
            </Button>
          </InputGroup.Addon>
        </InputGroup>
        {hasErrors && <HelpBlock>{errors}</HelpBlock>}
      </FormGroup>
    );
  }

  render() {
    const {
      values, defaultValue, isMultiple,
      isNew, disabled,
    } = this.props;
    const { editingMode, updating } = this.state;
    let newDefaultValues = [];
    if (isArray(defaultValue)) {
      newDefaultValues = defaultValue;
    } else if (defaultValue) {
      newDefaultValues = [defaultValue];
    }
    if (!isMultiple) {
      newDefaultValues = slice(newDefaultValues, 0, 1);
    }
    const newValues = reduce(values, (items, val) => {
      const isDefault = indexOf(newDefaultValues, val.value) >= 0;
      items.push({
        ...val,
        default: isDefault,
      });
      return items;
    }, []);

    return (
      <div>
        <ControlLabel>Value List</ControlLabel>
        <div className={`well well-sm ${styles.valueList}`}>
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Fragment>
              <table style={{ margin: 0 }} className="table table-condensed table-hover">
                {values.length > 0 &&
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th width="99%">Label</th>
                      <th className="text-center">Default</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                }
                <Droppable droppableId="value-list">
                  {(provided, snapshot) => (
                    <tbody
                      ref={provided.innerRef}
                      className={snapshot.isDraggingOver ? styles.draggingOver : null}
                    >
                      {newValues.map((value, index) => (
                        <Draggable key={value._id} draggableId={value._id} index={index}>
                          {(pv, ss) => (
                            <ValueItem
                              key={value._id}
                              innerRef={pv.innerRef}
                              isDragging={ss.isDragging}
                              draggableProps={pv.draggableProps}
                              dragHandleProps={pv.dragHandleProps}
                              value={value}
                              isMultiple={isMultiple}
                              requestRemoveValue={this.props.requestRemoveValue}
                              requestUpdateValue={this.props.requestUpdateValue}
                              requestUpdateDefault={this.props.requestUpdateDefault}
                              showCancelButton={!isNew}
                              disabled={disabled}
                              updatingDragging={updating}
                            />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
                <tbody>
                  <tr>
                    {newValues.length > 0 && <td>&nbsp;</td>}
                    <td colSpan={newValues.length === 0 ? 4 : 3}>
                      {!editingMode && this.renderAddBtn()}
                      {editingMode && this.renderAddForm()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Fragment>
          </DragDropContext>
        </div>
      </div>
    );
  }
}

ValueList.propTypes = {
  isMultiple: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  values: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
  })),
  requestNewValue: PropTypes.func,
  requestRemoveValue: PropTypes.func,
  requestUpdateValue: PropTypes.func,
  requestUpdateDefault: PropTypes.func,
  requestUpdatePositions: PropTypes.func,
  isNew: PropTypes.bool,
  disabled: PropTypes.bool,
};
ValueList.defaultProps = {
  values: [],
  isMultiple: false,
  defaultValue: null,
  isNew: false,
  disabled: false,
  requestNewValue: () => {},
  requestRemoveValue: () => Promise.reject(new Error('need handler')),
  requestUpdateValue: () => Promise.reject(new Error('need handler')),
  requestUpdateDefault: () => Promise.reject(new Error('need handler')),
  requestUpdatePositions: () => Promise.reject(new Error('need handler')),
};

export default ValueList;
